














































































import { VueComponent, InjectComponent, Component, Vue, Prop, ComponentConfig } from "@zoovu/runner-browser-api";
import { vTooltip } from "@zoovu/design-system/src/plugins";
import {
  AnimationsAndTransitionsConfiguration,
  ChoiceAnswerView,
  TransitionStyle,
} from "@zoovu/runner-web-design-base";
import { isMobile, isTablet } from "@zoovu/design-system/src/helpers/index";
import { sanitize } from "dompurify";
import { AnswerAnimationStyleClass, getAnimationClass } from "../styles/abstract/animation";
import { hideHorizontalOverflow, restoreHorizontalOverflow } from "../services/overflow-service";
import { getTransitionName } from "../styles/abstract/transition";
import { TooltipConfiguration, whitelistedAttributes } from "../types";
import InvisibleWrapper from "./invisible-wrapper.vue";

@Component({
  directives: {
    tooltip: vTooltip.directives.tooltip,
  },
  name: "ChoiceAnswerView",
  mixins: [ChoiceAnswerView],
})
export default class ChoiceAnswerViewExtended extends Vue {
  @ComponentConfig(AnimationsAndTransitionsConfiguration)
  animationsAndTransitionsConfiguration: AnimationsAndTransitionsConfiguration;

  @Prop()
  answerTextHeight: string;

  @Prop()
  imageHeight: string;

  infoTextShown = false;

  isMobile: boolean = isMobile();

  isTablet: boolean = isTablet();

  transitionDelayEnabled = false;

  get animationClass(): AnswerAnimationStyleClass {
    if (this.animationsAndTransitionsConfiguration.animationsEnabled) {
      return getAnimationClass(this.animationsAndTransitionsConfiguration.animationStyle.selected);
    }
    return AnswerAnimationStyleClass.NO_ANIMATION;
  }

  hideInfoText(): void {
    this.infoTextShown = false;
  }

  showInfoText(): void {
    this.infoTextShown = true;
  }

  get answerIndex(): number {
    return this.answer.question.answers.indexOf(this.answer);
  }

  get tooltipEnabled(): boolean {
    return !this.isMobile && !this.isTablet;
  }

  get infoTextTooltipConfiguration(): TooltipConfiguration {
    return {
      autoHide: false,
      container: this.containerDivId,
      content: sanitize(this.answer.infoText, { ADD_ATTR: whitelistedAttributes }),
      placement: "left",
      trigger: "hover click",
      delay: {
        show: 0,
        hide: 2000,
      },
    };
  }

  clickOnInfoText(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isMobile && !this.isTablet) return;
    this.showInfoText();
  }

  @InjectComponent("InfoTextMobile")
  InfoTextMobile: VueComponent;

  singleAnswerClass = "single-answer";

  selectAnswer = (event: Event): void => {
    const $label = event.target.querySelector("label");
    if ($label) {
      $label.click();
    }
  };

  get answerWrapperStyle(): Record<string, string> {
    return {
      ...(this.transitionDelayEnabled ? { transitionDelay: `${this.answerIndex * 100}ms` } : {}),
      width: this.answerWrapperWidth,
    };
  }

  get answerWrapperWidth(): string {
    const numberOfAnswers = this.answer.question.answers.length;
    const maxAllowedAnswersPerRow = 4;
    const perRow = numberOfAnswers < maxAllowedAnswersPerRow ? numberOfAnswers : maxAllowedAnswersPerRow;
    const gutter = 14;
    return `calc( ${100 / perRow}% - ${gutter}px )`;
  }

  get containerDivId(): string {
    return `#${this.$root.containerDivId} > div`;
  }

  get selectedTransitionStyle(): TransitionStyle {
    return this.animationsAndTransitionsConfiguration.transitionStyle.selected;
  }

  get transitionComponent(): string | VueComponent {
    if (
      this.animationsAndTransitionsConfiguration.transitionsEnabled &&
      (this.selectedTransitionStyle === TransitionStyle.SLIDE_LEFT ||
        this.selectedTransitionStyle === TransitionStyle.FADE_AND_SLIDE_LEFT)
    ) {
      return "transition";
    }
    return InvisibleWrapper;
  }

  get transitionName(): string {
    if (this.animationsAndTransitionsConfiguration.transitionsEnabled) {
      return getTransitionName(this.selectedTransitionStyle);
    }
    return "";
  }

  disableTransitionDelay(): void {
    this.transitionDelayEnabled = false;
  }

  enableTransitionDelay(): void {
    this.transitionDelayEnabled = true;
  }

  onAfterEnter(): void {
    if (this.answerIndex === this.answer.question.answers.length - 1) {
      restoreHorizontalOverflow();
    }

    this.disableTransitionDelay();
  }

  onBeforeEnter(): void {
    if (this.answerIndex === 0) {
      hideHorizontalOverflow();
    }

    this.enableTransitionDelay();
  }
}
